import vueI18n from '@/plugins/i18n';

const staticCloudname = process.env.VUE_APP_CLOUDINARY_STATIC_FILES_CLOUDNAME;
const staticCloudinaryUrl = `https://res.cloudinary.com/${staticCloudname}/image/upload/`;

const CONSTANTS = Object.freeze({
  // See https://cloudinary.com/documentation/image_transformations for info
  //
  // Cloudinary url structure:
  // https://res.cloudinary.com/<cloud_name>/<resource_type>/<type>/<transformations>/<version>/<public_id>.<format>
  //
  // Example:
  // https://res.cloudinary.com/helloguest/image/upload/w_200/icons/plus_circle.png
  //
  // // Required:
  // project (helloguest)
  // type is in most cases "upload"
  // resourcetype is in most cases "image"
  // <public_id>.<format> is basically filepath + filename
  //
  // Optional:
  // version
  // transformations (example: w_200)
  cloudinary: {
    url: staticCloudinaryUrl,
  },

  imagePlaceholder: 'missing.png',
  imagePlaceholderUrl: `${staticCloudinaryUrl}/w_350,c_fill/missing.png`,
  couponImagePlaceholderUrl: `${staticCloudinaryUrl}/add_image_new.png`,

  weekDays: [
    { number: 0, get name() { return vueI18n.t('cms.campaign.RepeatMondayChoice'); } },
    { number: 1, get name() { return vueI18n.t('cms.campaign.RepeatTuesdayChoice'); } },
    { number: 2, get name() { return vueI18n.t('cms.campaign.RepeatWednesayChoice'); } },
    { number: 3, get name() { return vueI18n.t('cms.campaign.RepeatThursdayChoice'); } },
    { number: 4, get name() { return vueI18n.t('cms.campaign.RepeatFridayChoice'); } },
    { number: 5, get name() { return vueI18n.t('cms.campaign.RepeatSaturdayChoice'); } },
    { number: 6, get name() { return vueI18n.t('cms.campaign.RepeatSundayChoice'); } },
  ],

  campaignDelayOptions: [
    { value: '', get name() { return vueI18n.t('cms.campaign.delay.NoDelayChoice'); } },
    { value: '00:30', get name() { return vueI18n.t('cms.campaign.delay.Delay30MinsChoice'); } },
    { value: '01:00', get name() { return vueI18n.t('cms.campaign.delay.Delay1HourChoice'); } },
    { value: '02:00', get name() { return vueI18n.t('cms.campaign.delay.Delay2HourChoice'); } },
    { value: '03:00', get name() { return vueI18n.t('cms.campaign.delay.Delay3HoursChoice'); } },
  ],

  shortWeekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'],

  // Validation stuff
  imageTypes: ['image/gif', 'image/jpeg', 'image/png'],
  minImageSize: 0.01, // in MB
  maxImageSize: 30, // in MB
  largeImageSize: 5, // in MB

  // Other stuff
  bearerKey: 'horecapro_access_bearer',

  // todo see if we can use color variables everywhere, #71ca1 is declared in 5 different places
  campaignStatusTypes: {
    new: { value: 'new', get text() { return vueI18n.t('cms.campaign.NewCampaignButton'); }, color: '#99e7f7' },
    scheduled: { value: 'scheduled', get text() { return vueI18n.t('cms.campaign.ScheduledCampaignButton'); }, color: '#f9e65c' },
    running: { value: 'running', get text() { return vueI18n.t('cms.campaign.ActiveCampaignButton'); }, color: '#00C853' },
  },

  languages: {
    en: { locale: 'en', get name() { return vueI18n.t('cms.language.en'); }, icon: 'flag_en' },
    nl: { locale: 'nl', get name() { return vueI18n.t('cms.language.nl'); }, icon: 'flag_nl' },
    de: { locale: 'de', get name() { return vueI18n.t('cms.language.de'); }, icon: 'flag_de' },
    fr: { locale: 'fr', get name() { return vueI18n.t('cms.language.fr'); }, icon: 'flag_fr' },
    es: { locale: 'es', get name() { return vueI18n.t('cms.language.es'); }, icon: 'flag_es' },
    pl: { locale: 'pl', get name() { return vueI18n.t('cms.language.pl'); }, icon: 'flag_pl' },
    it: { locale: 'it', get name() { return vueI18n.t('cms.language.it'); }, icon: 'flag_it' },
    pt: { locale: 'pt', get name() { return vueI18n.t('cms.language.pt'); }, icon: 'flag_pt' },
    id: { locale: 'id', get name() { return vueI18n.t('cms.language.id'); }, icon: 'flag_id' },
    zh: { locale: 'zh', get name() { return vueI18n.t('cms.language.zh'); }, icon: 'flag_zh' },
    ru: { locale: 'ru', get name() { return vueI18n.t('cms.language.ru'); }, icon: 'flag_ru' },
  },

  barcodeTypes: {
    EAN: {
      text: 'EAN',
      value: 'EAN',
      jsBarcodeType: 'EAN13',
      validate(v) {
        return +v && `${v}`.length === 13;
      },
    },
    UPC: {
      text: 'UPC',
      value: 'UPC',
      jsBarcodeType: 'UPC',
      validate(v) {
        return +v && `${v}`.length === 12;
      },
    },
  },

  couponCtaTypes: {
    redeem: {
      value: 'REDEEM',
      icon: 'doneRedeem',
      get text() {
        return vueI18n.t('cms.coupons.cta.Redeem');
      },
      get buttonText() {
        return vueI18n.t('cms.coupons.cta.RedeemButton');
      },
      get description() {
        return vueI18n.t('cms.coupons.cta.RedeemDescription');
      },
    },
    buy: {
      value: 'BUY_LINK',
      icon: 'buy',
      get text() {
        return vueI18n.t('cms.coupons.cta.BuyLink');
      },
      get buttonText() {
        return vueI18n.t('cms.coupons.cta.BuyLinkButton');
      },
      get description() {
        return vueI18n.t('cms.coupons.cta.BuyLinkDescription');
      },
    },
    info: {
      value: 'INFO_LINK',
      icon: 'information',
      get text() {
        return vueI18n.t('cms.coupons.cta.InfoLink');
      },
      get buttonText() {
        return vueI18n.t('cms.coupons.cta.InfoLinkButton');
      },
      get description() {
        return vueI18n.t('cms.coupons.cta.InfoLinkDescription');
      },
    },
    barcode: {
      value: 'BARCODE',
      icon: 'barcode',
      get text() {
        return vueI18n.t('cms.coupons.cta.BarCode');
      },
      get buttonText() {
        return vueI18n.t('cms.coupons.cta.BarCodeButton');
      },
      get description() {
        return vueI18n.t('cms.coupons.cta.BarCodeDescription');
      },
    },
    qrCode: {
      value: 'QR_CODE',
      icon: 'qrCode',
      get text() {
        return vueI18n.t('cms.coupons.cta.QRCode');
      },
      get buttonText() {
        return vueI18n.t('cms.coupons.cta.QRCodeButton');
      },
      get description() {
        return vueI18n.t('cms.coupons.cta.QRCodeDescription');
      },
    },
  },

  couponStatisticsTypes: {
    all: {
      value: 'ALL',
      icon: 'statsAllCoupons',
      get text() {
        return vueI18n.t('cms.statistics.type_filter.All');
      },
      get title() {
        return vueI18n.t('cms.statistics.type_filter.AllTitle');
      },
      get description() {
        return vueI18n.t('cms.statistics.type_filter.AllDescription');
      },
    },
    partnership: {
      value: 'PARTNERSHIP',
      icon: 'partnership',
      get text() {
        return vueI18n.t('cms.statistics.type_filter.Partnership');
      },
      get title() {
        return vueI18n.t('cms.statistics.type_filter.PartnershipTitle');
      },
      get description() {
        return vueI18n.t('cms.statistics.type_filter.PartnershipDescription');
      },
    },
    own: {
      value: 'OWN',
      icon: 'statsCoupon',
      get text() {
        return vueI18n.t('cms.statistics.type_filter.Own');
      },
      get title() {
        return vueI18n.t('cms.statistics.type_filter.OwnTitle');
      },
      get description() {
        return vueI18n.t('cms.statistics.type_filter.OwnDescription');
      },
    },
  },

  messageTypes: {
    default: { color: 'white', backgroundColor: '#100063' },
    success: { color: 'black', backgroundColor: '#6efd56' },
    warning: { color: 'white', backgroundColor: '#dbd553' },
    error: { color: 'white', backgroundColor: '#cb4646' },
  },

  onlineStatuses: [
    { color: '#4A4A4A', name: 'offline' },
    { color: '#00C853', name: 'online' },
  ],

  // We need to use an array of 5 vueI18n.t items so the horecadata admins can
  // choose how many texts to use. They are allowed to be empty, therefore we first
  // check if they exist with vueI18n.te
  infoConfig: {
    coupon: {
      icon: 'coupon',
      get cardHeader() { return vueI18n.t('cms.info.coupon.ModalHeader'); },
      get cardSubtitle() { return vueI18n.t('cms.info.coupon.ModalSubtitle'); },
      get contentHeaders() {
        return [
          vueI18n.te('cms.info.coupon.ContentHeader1') && vueI18n.t('cms.info.coupon.ContentHeader1'),
          vueI18n.te('cms.info.coupon.ContentHeader2') && vueI18n.t('cms.info.coupon.ContentHeader2'),
          vueI18n.te('cms.info.coupon.ContentHeader3') && vueI18n.t('cms.info.coupon.ContentHeader3'),
          vueI18n.te('cms.info.coupon.ContentHeader4') && vueI18n.t('cms.info.coupon.ContentHeader4'),
          vueI18n.te('cms.info.coupon.ContentHeader5') && vueI18n.t('cms.info.coupon.ContentHeader5'),
          vueI18n.te('cms.info.coupon.ContentHeader6') && vueI18n.t('cms.info.coupon.ContentHeader6'),
          vueI18n.te('cms.info.coupon.ContentHeader7') && vueI18n.t('cms.info.coupon.ContentHeader7'),
        ].filter(v => v);
      },
      get contentTexts() {
        return [
          vueI18n.te('cms.info.coupon.ContentTexts1') && vueI18n.t('cms.info.coupon.ContentTexts1'),
          vueI18n.te('cms.info.coupon.ContentTexts2') && vueI18n.t('cms.info.coupon.ContentTexts2'),
          vueI18n.te('cms.info.coupon.ContentTexts3') && vueI18n.t('cms.info.coupon.ContentTexts3'),
          vueI18n.te('cms.info.coupon.ContentTexts4') && vueI18n.t('cms.info.coupon.ContentTexts4'),
          vueI18n.te('cms.info.coupon.ContentTexts5') && vueI18n.t('cms.info.coupon.ContentTexts5'),
          vueI18n.te('cms.info.coupon.ContentTexts6') && vueI18n.t('cms.info.coupon.ContentTexts6'),
          vueI18n.te('cms.info.coupon.ContentTexts7') && vueI18n.t('cms.info.coupon.ContentTexts7'),
        ];
      },
    },
    notification: {
      icon: 'notificationNew',
      get cardHeader() { return vueI18n.t('cms.info.notification.ModalHeader'); },
      get cardSubtitle() { return vueI18n.t('cms.info.notification.ModalSubtitle'); },
      get contentHeaders() {
        return [
          vueI18n.te('cms.info.notification.ContentHeader1') && vueI18n.t('cms.info.notification.ContentHeader1'),
          vueI18n.te('cms.info.notification.ContentHeader2') && vueI18n.t('cms.info.notification.ContentHeader2'),
          vueI18n.te('cms.info.notification.ContentHeader3') && vueI18n.t('cms.info.notification.ContentHeader3'),
          vueI18n.te('cms.info.notification.ContentHeader4') && vueI18n.t('cms.info.notification.ContentHeader4'),
          vueI18n.te('cms.info.notification.ContentHeader5') && vueI18n.t('cms.info.notification.ContentHeader5'),
          vueI18n.te('cms.info.notification.ContentHeader6') && vueI18n.t('cms.info.notification.ContentHeader6'),
          vueI18n.te('cms.info.notification.ContentHeader7') && vueI18n.t('cms.info.notification.ContentHeader7'),
        ];
      },
      get contentTexts() {
        return [
          vueI18n.te('cms.info.notification.ContentTexts1') && vueI18n.t('cms.info.notification.ContentTexts1'),
          vueI18n.te('cms.info.notification.ContentTexts2') && vueI18n.t('cms.info.notification.ContentTexts2'),
          vueI18n.te('cms.info.notification.ContentTexts3') && vueI18n.t('cms.info.notification.ContentTexts3'),
          vueI18n.te('cms.info.notification.ContentTexts4') && vueI18n.t('cms.info.notification.ContentTexts4'),
          vueI18n.te('cms.info.notification.ContentTexts5') && vueI18n.t('cms.info.notification.ContentTexts5'),
          vueI18n.te('cms.info.notification.ContentTexts6') && vueI18n.t('cms.info.notification.ContentTexts6'),
          vueI18n.te('cms.info.notification.ContentTexts7') && vueI18n.t('cms.info.notification.ContentTexts7'),
        ];
      },
    },
    datetime: {
      icon: 'datetime',
      get cardHeader() { return vueI18n.t('cms.info.datetime.ModalHeader'); },
      get cardSubtitle() { return vueI18n.t('cms.info.datetime.ModalSubtitle'); },
      get contentHeaders() {
        return [
          vueI18n.te('cms.info.datetime.ContentHeader1') && vueI18n.t('cms.info.datetime.ContentHeader1'),
          vueI18n.te('cms.info.datetime.ContentHeader2') && vueI18n.t('cms.info.datetime.ContentHeader2'),
          vueI18n.te('cms.info.datetime.ContentHeader3') && vueI18n.t('cms.info.datetime.ContentHeader3'),
          vueI18n.te('cms.info.datetime.ContentHeader4') && vueI18n.t('cms.info.datetime.ContentHeader4'),
          vueI18n.te('cms.info.datetime.ContentHeader5') && vueI18n.t('cms.info.datetime.ContentHeader5'),
          vueI18n.te('cms.info.datetime.ContentHeader6') && vueI18n.t('cms.info.datetime.ContentHeader6'),
          vueI18n.te('cms.info.datetime.ContentHeader7') && vueI18n.t('cms.info.datetime.ContentHeader7'),
        ];
      },
      get contentTexts() {
        return [
          vueI18n.te('cms.info.datetime.ContentTexts1') && vueI18n.t('cms.info.datetime.ContentTexts1'),
          vueI18n.te('cms.info.datetime.ContentTexts2') && vueI18n.t('cms.info.datetime.ContentTexts2'),
          vueI18n.te('cms.info.datetime.ContentTexts3') && vueI18n.t('cms.info.datetime.ContentTexts3'),
          vueI18n.te('cms.info.datetime.ContentTexts4') && vueI18n.t('cms.info.datetime.ContentTexts4'),
          vueI18n.te('cms.info.datetime.ContentTexts5') && vueI18n.t('cms.info.datetime.ContentTexts5'),
          vueI18n.te('cms.info.datetime.ContentTexts6') && vueI18n.t('cms.info.datetime.ContentTexts6'),
          vueI18n.te('cms.info.datetime.ContentTexts7') && vueI18n.t('cms.info.datetime.ContentTexts7'),
        ];
      },
    },
    campaignStatus: {
      icon: 'launch',
      get cardHeader() { return vueI18n.t('cms.info.launch.ModalHeader'); },
      get cardSubtitle() { return vueI18n.t('cms.info.launch.ModalSubtitle'); },
      get contentHeaders() {
        return [
          vueI18n.te('cms.info.launch.ContentHeader1') && vueI18n.t('cms.info.launch.ContentHeader1'),
          vueI18n.te('cms.info.launch.ContentHeader2') && vueI18n.t('cms.info.launch.ContentHeader2'),
          vueI18n.te('cms.info.launch.ContentHeader3') && vueI18n.t('cms.info.launch.ContentHeader3'),
          vueI18n.te('cms.info.launch.ContentHeader4') && vueI18n.t('cms.info.launch.ContentHeader4'),
          vueI18n.te('cms.info.launch.ContentHeader5') && vueI18n.t('cms.info.launch.ContentHeader5'),
          vueI18n.te('cms.info.launch.ContentHeader6') && vueI18n.t('cms.info.launch.ContentHeader6'),
          vueI18n.te('cms.info.launch.ContentHeader7') && vueI18n.t('cms.info.launch.ContentHeader7'),
        ].filter(v => v);
      },
      get contentTexts() {
        return [
          vueI18n.te('cms.info.launch.ContentTexts1') && vueI18n.t('cms.info.launch.ContentTexts1'),
          vueI18n.te('cms.info.launch.ContentTexts2') && vueI18n.t('cms.info.launch.ContentTexts2'),
          vueI18n.te('cms.info.launch.ContentTexts3') && vueI18n.t('cms.info.launch.ContentTexts3'),
          vueI18n.te('cms.info.launch.ContentTexts4') && vueI18n.t('cms.info.launch.ContentTexts4'),
          vueI18n.te('cms.info.launch.ContentTexts5') && vueI18n.t('cms.info.launch.ContentTexts5'),
          vueI18n.te('cms.info.launch.ContentTexts6') && vueI18n.t('cms.info.launch.ContentTexts6'),
          vueI18n.te('cms.info.launch.ContentTexts7') && vueI18n.t('cms.info.launch.ContentTexts7'),
        ].filter(v => v);
      },
    },
    dealCalculator: {
      icon: 'calculator',
      get cardHeader() { return vueI18n.t('cms.info.dealCalculator.ModalHeader'); },
      get cardSubtitle() { return vueI18n.t('cms.info.dealCalculator.ModalSubtitle'); },
      get contentHeaders() {
        return [
          vueI18n.te('cms.info.dealCalculator.ContentHeader1') && vueI18n.t('cms.info.dealCalculator.ContentHeader1'),
          vueI18n.te('cms.info.dealCalculator.ContentHeader2') && vueI18n.t('cms.info.dealCalculator.ContentHeader2'),
          vueI18n.te('cms.info.dealCalculator.ContentHeader3') && vueI18n.t('cms.info.dealCalculator.ContentHeader3'),
          vueI18n.te('cms.info.dealCalculator.ContentHeader4') && vueI18n.t('cms.info.dealCalculator.ContentHeader4'),
          vueI18n.te('cms.info.dealCalculator.ContentHeader5') && vueI18n.t('cms.info.dealCalculator.ContentHeader5'),
          vueI18n.te('cms.info.dealCalculator.ContentHeader6') && vueI18n.t('cms.info.dealCalculator.ContentHeader6'),
          vueI18n.te('cms.info.dealCalculator.ContentHeader7') && vueI18n.t('cms.info.dealCalculator.ContentHeader7'),
        ].filter(v => v);
      },
      get contentTexts() {
        return [
          vueI18n.te('cms.info.dealCalculator.ContentTexts1') && vueI18n.t('cms.info.dealCalculator.ContentTexts1'),
          vueI18n.te('cms.info.dealCalculator.ContentTexts2') && vueI18n.t('cms.info.dealCalculator.ContentTexts2'),
          vueI18n.te('cms.info.dealCalculator.ContentTexts3') && vueI18n.t('cms.info.dealCalculator.ContentTexts3'),
          vueI18n.te('cms.info.dealCalculator.ContentTexts4') && vueI18n.t('cms.info.dealCalculator.ContentTexts4'),
          vueI18n.te('cms.info.dealCalculator.ContentTexts5') && vueI18n.t('cms.info.dealCalculator.ContentTexts5'),
          vueI18n.te('cms.info.dealCalculator.ContentTexts6') && vueI18n.t('cms.info.dealCalculator.ContentTexts6'),
          vueI18n.te('cms.info.dealCalculator.ContentTexts7') && vueI18n.t('cms.info.dealCalculator.ContentTexts7'),
        ].filter(v => v);
      },
    },
  },

  metrics: [
    {
      get text() { return vueI18n.t('cms.dashboard.UniqueVisitorLabel'); },
      key: 'uniqueVisitors',
      color: '#A5E7F2',
      isMaxColor: '#6db0bb',
      includeInGraph: true,
      suffix: '',
      placeholder: 1000,
    },
    {
      get text() { return vueI18n.t('cms.dashboard.SentNotificationsLabel'); },
      key: 'sentNotifications',
      color: '#3A6EF2',
      isMaxColor: '#244eba',
      includeInGraph: true,
      suffix: '',
      placeholder: 350,
    },
    {
      get text() { return vueI18n.t('cms.dashboard.ClickThroughLabel'); },
      key: 'clickThroughRate',
      color: '#9B52A7',
      isMaxColor: '#662e6f',
      includeInGraph: true,
      suffix: '%',
      placeholder: -15,
    },
    {
      get text() { return vueI18n.t('cms.dashboard.AverageCouponViewTime'); },
      key: 'averageCouponViewTime',
      icon: 'clock',
      format: 'time',
      placeholder: -120,
    },
    {
      get text() { return vueI18n.t('cms.dashboard.ClicksToActionLabel'); },
      key: 'redeemedCoupons',
      icon: 'star',
      placeholder: -31,
    },
  ],
});

export default CONSTANTS;
